<template v-once>
  <main-layout>
    <template v-slot:top-section>
      <top-section-wrapper>
        <top-section-content show-right-side>
          <template v-slot:top-section-heading>
            <font-title text="Terms of Service" />
          </template>
        </top-section-content>
      </top-section-wrapper>
    </template>

    <div class="terms-wrapper">
      <p
        v-for="paragraph in intro"
        :key="paragraph"
        class="intro-paragraph"
        v-html="paragraph"
      >
      </p>

      <section
        v-for="section in sections"
        :key="section.id"
        class="terms-section"
      >
        <h3 class="section-title">{{ section.id }}. {{ section.title }}</h3>
        <ol class="content-list">
          <li
            v-for="item in section.items"
            :key="item.number"
            class="content-item"
          >
            <p
              class="content-text"
              v-html="`<strong>${item.number}.</strong> ${item.text}`"
            />
            <ul
              v-if="item.bullets && item.bullets.length"
              class="nested-list"
            >
              <li
                v-for="bullet in item.bullets"
                :key="bullet"
              >
                {{ bullet }}
              </li>
            </ul>
          </li>
        </ol>
      </section>

      <section class="terms-section">
        <h3 class="section-title">17. Contact</h3>
        <p class="content-text">
          If you have any questions about these Terms, please contact us:
          <a class="contact-link" href="mailto:info@a2dao.com">info@a2dao.com</a>.
        </p>
      </section>
    </div>
  </main-layout>
</template>

<script>
import TopSectionWrapper from '@/components/TopSectionWrapper';
import MainLayout from '@/layouts/main';
import TopSectionContent from '@/components/TopSectionContent';
import FontTitle from '@/components/FontTitle';

export default {
  name: 'TermsOfServicePage',
  components: {
    FontTitle,
    TopSectionWrapper,
    MainLayout,
    TopSectionContent
  },
  data() {
    return {
      intro: [
        'Please read these <strong>Terms of Service</strong> (the "Terms") carefully before using the A2DAO website and platform located at <a class="contact-link" href="https://a2dao.com/" target="_blank" rel="noopener noreferrer">https://a2dao.com/</a> (the "Platform").',
        'These Terms are a legal agreement between you ("you", "User", or "Participant") and A2DAO ("A2DAO", "we", "us", or the "Platform Operator") governing your access to and use of the Platform and any related services, features, content, communications, and functionality made available through the Platform (collectively, the "Services").',
        'By accessing or using the Platform, you confirm that you have read, understood, and agree to be bound by these Terms. <strong>If you do not agree, you must not access or use the Platform.</strong>'
      ],
      sections: [
        {
          id: '1',
          title: 'Definitions',
          items: [
            {
              number: '1.1',
              text: '<strong>Platform</strong> means the A2DAO website and Web3 platform that provides access to community participation and deal opportunities related to crypto/Web3 projects.'
            },
            {
              number: '1.2',
              text: '<strong>Services</strong> means the services, tools, features, information, content, communications, and functionality offered by A2DAO through the Platform.'
            },
            {
              number: '1.3',
              text: '<strong>User or Participant</strong> means an individual who accesses or uses the Platform and/or registers on the Platform using blockchain-based identification tools approved by the Platform.'
            },
            {
              number: '1.4',
              text: '<strong>Projects</strong> means Web3 companies, DAOs, or other entities that may raise funding and/or distribute digital assets, including utility or service tokens and/or NFTs, to participants.'
            },
            {
              number: '1.5',
              text: '<strong>Deal</strong> means any event, activity, participation opportunity, allocation process, sale, distribution, campaign, or similar arrangement made available through the Platform in relation to a Project.'
            },
            {
              number: '1.6',
              text: "<strong>Leaderboard Rank</strong> means an indicator of a Participant's activity and involvement within the A2DAO community, calculated and displayed by the Platform."
            },
            {
              number: '1.7',
              text: "<strong>Rating Points</strong> means an indicator calculated by the Platform which may reflect a Participant's ATD token holdings and/or their activity involving ATD tokens, and which may be used to determine eligibility and/or allocation parameters for certain Deals."
            },
            {
              number: '1.8',
              text: '<strong>Digital Assets</strong> means cryptocurrencies, tokens, NFTs, and any other blockchain-based digital assets.'
            }
          ]
        },
        {
          id: '2',
          title: 'Eligibility and Account',
          items: [
            {
              number: '2.1',
              text: 'You represent that you are an individual with full legal capacity to enter into these Terms and to use the Platform and Services.'
            },
            {
              number: '2.2',
              text: 'You are responsible for maintaining the confidentiality of any credentials, wallets, keys, devices, or other access methods you use to access the Platform. <strong>You are solely responsible for all activity conducted through your account, wallet, or access methods.</strong>'
            },
            {
              number: '2.3',
              text: 'We may refuse registration, suspend, restrict, or terminate access to the Platform at any time in accordance with these Terms, including for security, compliance, or risk-management reasons.'
            }
          ]
        },
        {
          id: '3',
          title: 'Nature of the Platform and Services',
          items: [
            {
              number: '3.1',
              text: 'The Platform provides informational and technical tools that may facilitate community participation and access to Deal opportunities related to Projects. A2DAO is not the issuer of Project tokens/NFTs unless explicitly stated, and A2DAO does not control Projects or their actions.'
            },
            {
              number: '3.2',
              text: 'Participation in Platform events and activities is voluntary and at your sole discretion.'
            },
            {
              number: '3.3',
              text: 'Deal formats and rules may vary and may include, without limitation, allocation based on Leaderboard Rank and/or Rating Points, and/or "First Come First Serve" ("FCFS") formats, until the Deal limit is reached.'
            },
            {
              number: '3.4',
              text: 'The Platform Operator may determine, set, modify, or cancel Deal parameters, including eligibility criteria, timelines, limits per participant, allocation rules, and any other indicators or conditions, at its sole discretion.'
            },
            {
              number: '3.5',
              text: 'Deal terms and changes may be announced via official A2DAO channels (including Telegram, X/Twitter, Discord, and other official communication channels) and/or on the Platform website. You are responsible for monitoring official announcements.'
            }
          ]
        },
        {
          id: '4',
          title: 'User Responsibilities, Due Diligence, and Risk Acknowledgements',
          items: [
            {
              number: '4.1',
              text: 'By using the Platform and participating in any Deal, you confirm that you act based on your own informed, conscious, and independent decision.'
            },
            {
              number: '4.2',
              text: 'You are solely responsible for conducting your own due diligence regarding any Project and any Deal, including legal, tax, accounting, regulatory, and financial considerations. <strong>A2DAO does not provide legal, tax, financial, or investment advice.</strong>'
            },
            {
              number: '4.3',
              text: 'You acknowledge that <strong>Digital Assets are highly volatile and risky</strong>. Their value may change significantly, and you may lose some or all of the value of any Digital Assets you acquire or hold.'
            },
            {
              number: '4.4',
              text: 'You acknowledge that the value of tokens and NFTs is not controlled by the Platform and may be materially and adversely affected by numerous risk factors, including market conditions, liquidity, technical issues, regulatory changes, Project actions, and third-party failures.'
            },
            {
              number: '4.5',
              text: 'You acknowledge that blockchain transactions are generally irreversible, that wallet security is your responsibility, and that <strong>loss of access to your wallet (including loss of private keys) may result in permanent loss of Digital Assets.</strong>'
            }
          ]
        },
        {
          id: '5',
          title: '"As Is" and "As Available" Disclaimer',
          items: [
            {
              number: '5.1',
              text: 'The Platform, Services, and any related content are provided on an "as is" and "as available" basis, unless otherwise specified in writing.'
            },
            {
              number: '5.2',
              text: 'While we strive to maintain accurate and up-to-date information, we do not warrant that the Platform, Services, or any content will be uninterrupted, error-free, complete, accurate, or current.'
            },
            {
              number: '5.3',
              text: 'We may remove or edit content, including on affiliated resources (such as official social media pages and messenger channels), at our sole discretion.'
            }
          ]
        },
        {
          id: '6',
          title: 'KYC/AML and Compliance Requests (Project-Dependent)',
          items: [
            {
              number: '6.1',
              text: 'Some Projects may require identity verification and/or compliance checks (including "Know Your Customer" (KYC) and anti-money laundering (AML) checks) as a condition of participation in a Deal or receipt of Digital Assets.'
            },
            {
              number: '6.2',
              text: 'Where applicable, you agree to provide accurate and complete information and documentation requested by the relevant Project and/or its designated service providers. Failure to provide required information may result in loss of eligibility for a Deal, reduced allocation, delay, or inability to receive Digital Assets.'
            },
            {
              number: '6.3',
              text: 'A2DAO may facilitate the communication of such requirements through the Platform, but KYC/AML checks (when required) may be conducted by Projects and/or third-party providers. You acknowledge that such requirements may vary by Project and may change over time.'
            }
          ]
        },
        {
          id: '7',
          title: 'Changes to Deals; Project Changes; Force Majeure',
          items: [
            {
              number: '7.1',
              text: 'Because Deals and related information are provided "as is", A2DAO is not responsible for changes made by Projects or third parties, including (without limitation) changes to tokenomics, TGE dates, vesting or lock-up schedules, allocations, distribution timelines, mint parameters, or any other Project conditions.'
            },
            {
              number: '7.2',
              text: 'You acknowledge that token or NFT distribution may be delayed, rescheduled, modified, or cancelled due to Project decisions, technical issues, blockchain congestion, third-party service outages, security incidents, or force majeure.'
            },
            {
              number: '7.3',
              text: 'To the maximum extent permitted by applicable law, <strong>A2DAO will not be liable to you</strong> for any losses arising from such changes, delays, or cancellations.'
            }
          ]
        },
        {
          id: '8',
          title: 'Modifications, Suspension, and Interruptions',
          items: [
            {
              number: '8.1',
              text: 'We reserve the right to change, modify, suspend, discontinue, or remove any portion of the Platform or Services at any time for any reason, without notice.'
            },
            {
              number: '8.2',
              text: 'The Platform may from time to time be interrupted or encounter technical difficulties due to maintenance, updates, hardware/software issues, security incidents, network conditions, or other circumstances beyond our reasonable control.'
            },
            {
              number: '8.3',
              text: 'We are not responsible for any inability to access or use the Platform due to downtime, disruptions, or technical errors.'
            }
          ]
        },
        {
          id: '9',
          title: 'Management and Enforcement',
          items: [
            {
              number: '9.1',
              text: 'We reserve the right to:',
              bullets: [
                'monitor the Platform for violations of these Terms;',
                'investigate suspected breaches and take appropriate actions;',
                'refuse, restrict, limit, suspend, or terminate access to the Platform, including disabling accounts or access methods to the extent technically feasible;',
                'take measures to protect the Platform, our users, and our rights and property, and to ensure proper operation of the Platform.'
              ]
            },
            {
              number: '9.2',
              text: 'We may cooperate with law enforcement or regulatory requests where required by applicable law.'
            }
          ]
        },
        {
          id: '10',
          title: 'Intellectual Property Rights',
          items: [
            {
              number: '10.1',
              text: 'You acknowledge that all intellectual property rights in and to the Platform and Services (including text, graphics, branding, design, software, and other content) belong to A2DAO or its licensors, and are protected by applicable intellectual property laws.'
            },
            {
              number: '10.2',
              text: 'You are granted a limited right to use the Platform in accordance with these Terms. Except for this limited right, you do not acquire any rights in or to the Platform.'
            },
            {
              number: '10.3',
              text: 'You have no right to access the Platform in source code form. You must not modify, reproduce, distribute, or create derivative works from any materials from the Platform except as expressly permitted by these Terms or applicable law.'
            },
            {
              number: '10.4',
              text: 'You must not use any illustrations, photographs, video or audio sequences, or any graphics separately from accompanying text where applicable.'
            }
          ]
        },
        {
          id: '11',
          title: 'Restrictions and Acceptable Use',
          items: [
            {
              number: '11.1',
              text: 'You may use the Platform only for lawful purposes.'
            },
            {
              number: '11.2',
              text: 'You must not use the Platform:',
              bullets: [
                'in any way that breaches any applicable local, national, or international law or regulation;',
                'in any way that is unlawful or fraudulent, or has any unlawful or fraudulent purpose or effect;',
                'to transmit, upload, or distribute any malicious code, including viruses, trojans, worms, spyware, adware, or similar harmful programs;',
                'to interfere with, disrupt, or attempt to gain unauthorized access to the Platform, accounts, networks, systems, or data.'
              ]
            },
            {
              number: '11.3',
              text: 'You also agree not to:',
              bullets: [
                'reproduce, duplicate, copy, or re-sell any part of the Platform in violation of these Terms;',
                'access without authority, interfere with, damage, or disrupt any part of the Platform or any equipment or network on which it is stored;',
                'disassemble, decompile, reverse-engineer, or create derivative works based on any part of the Platform or its source code;',
                'use framing, mirroring, scraping, spidering, or other technological means to reproduce or provide access to Platform content without our prior written consent;',
                'disrupt, disable, or impair the proper working of the Platform or our servers, including through hacking, cyber-attacks (including denial-of-service attacks), tampering, or reprogramming.'
              ]
            },
            {
              number: '11.4',
              text: 'You must comply with all applicable technology control or export laws and regulations.'
            }
          ]
        },
        {
          id: '12',
          title: 'Limitation of Liability',
          items: [
            {
              number: '12.1',
              text: 'You expressly agree that your use of the Services and the Platform is <strong>at your sole risk</strong>.'
            },
            {
              number: '12.2',
              text: 'To the maximum extent permitted by applicable law, A2DAO will not be liable to you, whether in contract, tort (including negligence), breach of statutory duty, or otherwise, for any losses arising under or in connection with these Terms or your use of (or inability to use) the Platform or Services, including (without limitation):',
              bullets: [
                'loss of revenue, profits, sales, business, or anticipated savings;',
                'business interruption;',
                'loss, corruption, or unavailability of data or information;',
                'loss of business opportunity, goodwill, or reputation; or',
                'any indirect, consequential, special, or incidental loss or damage.'
              ]
            },
            {
              number: '12.3',
              text: 'Except as expressly stated in these Terms, all conditions, warranties, representations, or other terms that might otherwise be implied by statute, common law, or otherwise are excluded to the fullest extent permitted by law.'
            }
          ]
        },
        {
          id: '13',
          title: 'Termination',
          items: [
            {
              number: '13.1',
              text: 'We may suspend or terminate your access to the Platform at any time, with or without notice, if we reasonably believe:',
              bullets: [
                'you have violated these Terms;',
                'your use of the Platform creates security, legal, or compliance risks; or',
                'suspension/termination is necessary to protect the Platform, Projects, users, or third parties.'
              ]
            },
            {
              number: '13.2',
              text: 'Upon termination, the license granted to you under these Terms ends immediately, and <strong>you must stop using the Platform.</strong>'
            }
          ]
        },
        {
          id: '14',
          title: 'Assignment and Third-Party Rights',
          items: [
            {
              number: '14.1',
              text: 'You may transfer your rights or obligations under these Terms only with our prior written consent.'
            },
            {
              number: '14.2',
              text: 'These Terms are personal to the parties and are not intended to benefit or be enforceable by any third party.'
            }
          ]
        },
        {
          id: '15',
          title: 'Severability',
          items: [
            {
              number: '15.1',
              text: 'Each provision of these Terms operates separately. If any provision is found to be unlawful or unenforceable, the remaining provisions will remain in full force and effect.'
            }
          ]
        },
        {
          id: '16',
          title: 'Entire Agreement',
          items: [
            {
              number: '16.1',
              text: 'These Terms constitute the entire agreement between you and A2DAO regarding your use of the Platform and Services and supersede any prior or contemporaneous understandings or agreements on the same subject matter.'
            }
          ]
        }
      ]
    };
  },
  head: {
    meta: [
      {
        name: 'description',
        content: 'Terms of Service for using the A2DAO platform and services.'
      },
    ],
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/mixins";

.terms-wrapper {
  border-radius: 10px;
  padding: 0 100px 115px 120px;
  margin-top: -100px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  color: #000;

  @include respond-below(md) {
    padding: 55px 28px;
    margin-top: -50px;
  }

  @include respond-below(sm) {
    padding: 55px 12px;
    margin-top: 0;
  }

  @include respond-below(xs) {
    padding: 55px 0;
    margin-top: 0;
  }

}

.intro-paragraph,
.content-text {
  font-family: "Space Mono";
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 1.6;
  letter-spacing: 0.02em;
  margin: 0;

  @include respond-below(sm) {
    font-size: 16px;
    line-height: 1.5;
  }
}

.intro-paragraph {
  margin-bottom: 18px;
}

.terms-section {
  margin-top: 34px;
}

.section-title {
  margin: 0 0 14px;
  font-family: "Space Mono";
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.3;
  letter-spacing: 0.05em;
  color: #000;

  @include respond-below(sm) {
    font-size: 20px;
  }
}

.content-list {
  margin: 0;
  padding-left: 28px;
  list-style: none;
}

.content-item {
  margin-bottom: 14px;
}

.nested-list {
  margin: 10px 0 0;
  padding-left: 40px;
  margin-left: 10px;
  list-style: disc;

  li {
    font-family: "Space Mono";
    font-style: normal;
    font-weight: 400;
    font-size: 18px;
    line-height: 1.6;
    letter-spacing: 0.02em;
    margin-bottom: 8px;

    @include respond-below(sm) {
      font-size: 16px;
      line-height: 1.5;
    }
  }
}

.intro-paragraph :deep(strong),
.content-text :deep(strong) {
  font-weight: 700;
}

.contact-link,
:deep(.contact-link) {
  color: #000;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:visited {
    color: #000;
  }
}
</style>
